// src/pages/Billing/Billing.js
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    TextField,
    CircularProgress,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Alert,
    Tabs,
    Tab
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CreditCardOffIcon from '@mui/icons-material/CreditCardOff';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import Layout from '../../components/Layout';
import SubscriptionPlans from '../../components/SubscriptionPlans';
import SubscriptionManagement from '../../components/SubscriptionManagement';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import HistoryIcon from '@mui/icons-material/History';

import subscriptionApi from '../../services/subscriptionApi';
import config from '../../Config';
import * as Sentry from "@sentry/react";
import dayjs from 'dayjs';
import { isLoggedIn } from "../../utils/utils";
import { useNavigate } from 'react-router-dom';

const calculatePrice = (points) => {
    // Tier 1: 0-1000 points at $0.04 each
    // Tier 2: 1001-10000 points at $0.035 each
    // Tier 3: 10001-50000 points at $0.03 each
    // Tier 4: 50001+ points at $0.025 each

    if (points <= 1000) {
        return points * 0.04;
    } else if (points <= 10000) {
        return points * 0.035;
    } else if (points <= 50000) {
        return points * 0.03;
    } else {
        return points * 0.025;
    }
};

// Calculate the minimum number of points needed for a $10 payment
const calculateMinPoints = () => {
    // At $0.04 per point, we need 250 points to reach $10
    return 250;
};

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
    }).format(amount);
};

const BillingPage = () => {
    const minPoints = calculateMinPoints();
    const navigate = useNavigate();
    
    // Tab state - Start with subscriptions (index 0)
    const [activeTab, setActiveTab] = useState(0);
    
    // One-time purchase states
    const [loading, setLoading] = useState(false);
    const [pointsInput, setPointsInput] = useState(minPoints);
    const [estimatedPrice, setEstimatedPrice] = useState(calculatePrice(minPoints));
    const [belowMinimum, setBelowMinimum] = useState(false);
    const [paymentHistory, setPaymentHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(true);
    const [token, setToken] = useState(null);
    const [hasTokens, setHasTokens] = useState(false);
    const [error, setError] = useState(null);
    
    // Subscription states
    const [userSubscriptions, setUserSubscriptions] = useState(null);
    const [loadingSubscriptions, setLoadingSubscriptions] = useState(true);
    const [subscriptionError, setSubscriptionError] = useState(null);
    const [subscriptionHistory, setSubscriptionHistory] = useState([]);
    const [loadingSubscriptionHistory, setLoadingSubscriptionHistory] = useState(true);
    
    // Auth state
    const [user, setUser] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem("user"));
        } catch {
            return null;
        }
    });
    const [isAuthChecked, setIsAuthChecked] = useState(false);

    const fetchToken = useCallback(async () => {
        if (!user?.token) return;
        
        try {
            const response = await fetch(`${config.apiBaseUrl}/token/statistic`, {
                headers: {
                    'Content-Type': 'application/json',
                    'user-authorization': user.token
                }
            });

            if (response.ok) {
                const data = await response.json();
                if (data && data.length > 0) {
                    setToken(data[0]);
                    setHasTokens(true); // Set hasTokens to true if at least one token exists
                } else {
                    setHasTokens(false);
                }
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error("Failed to fetch token:", error);
        }
    }, [user?.token]);

    const fetchPaymentHistory = useCallback(async () => {
        if (!user?.token) return;
        
        setLoadingHistory(true);
        try {
            const response = await fetch(`${config.apiBaseUrl}/payment/history`, {
                headers: {
                    'Content-Type': 'application/json',
                    'user-authorization': user.token
                }
            });

            if (response.ok) {
                const data = await response.json();
                setPaymentHistory(data);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error("Failed to fetch payment history:", error);
        } finally {
            setLoadingHistory(false);
        }
    }, [user?.token]);

    const fetchUserSubscriptions = useCallback(async () => {
        setLoadingSubscriptions(true);
        setSubscriptionError(null);
        try {
            const subscriptions = await subscriptionApi.getUserSubscriptions();
            // Handle case where API returns null (no subscription) vs array of subscriptions
            setUserSubscriptions(subscriptions);
        } catch (error) {
            Sentry.captureException(error);
            setSubscriptionError('Failed to load subscriptions');
            console.error("Failed to fetch user subscriptions:", error);
            // Set to null on error to avoid trying to access .length on undefined
            setUserSubscriptions(null);
        } finally {
            setLoadingSubscriptions(false);
        }
    }, []);

    const fetchSubscriptionHistory = useCallback(async () => {
        setLoadingSubscriptionHistory(true);
        try {
            const history = await subscriptionApi.getSubscriptionHistory();
            setSubscriptionHistory(history || []);
        } catch (error) {
            Sentry.captureException(error);
            console.error("Failed to fetch subscription history:", error);
            setSubscriptionHistory([]);
        } finally {
            setLoadingSubscriptionHistory(false);
        }
    }, []);

    // Auth check effect
    useEffect(() => {
        if (!user || !user.token) {
            navigate("/login");
            return;
        }
        setIsAuthChecked(true);
    }, [user, navigate]);

    useEffect(() => {
        if (!isAuthChecked || !user?.token) {
            return;
        }

        fetchToken();
        fetchPaymentHistory();
        fetchUserSubscriptions();
        fetchSubscriptionHistory();
    }, [isAuthChecked, user?.token, fetchToken, fetchPaymentHistory, fetchUserSubscriptions, fetchSubscriptionHistory]);

    useEffect(() => {
        // Update price when points change
        const price = calculatePrice(pointsInput);
        setEstimatedPrice(price);
    }, [pointsInput]);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handlePointsChange = (event) => {
        const value = parseInt(event.target.value, 10);
        if (!isNaN(value) && value > 0 && value <= 9999999) {
            setPointsInput(value);
            // Set belowMinimum flag if value is less than the minimum required
            setBelowMinimum(value < minPoints);
        } else if (event.target.value === '') {
            // Allow empty field for better UX
            setPointsInput('');
            setBelowMinimum(true);
        }
    };

    const handlePurchase = async () => {
        if (!user?.token) {
            setError("Authentication required. Please log in again.");
            navigate("/login");
            return;
        }
        
        setLoading(true);
        setError(null);

        // Check if the entered points meet the minimum requirement
        if (pointsInput < minPoints) {
            setError(`Minimum purchase is ${minPoints} credits (${formatCurrency(10)} minimum payment)`);
            setLoading(false);
            return;
        }

        try {
            const response = await fetch(`${config.apiBaseUrl}/payment/checkout`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'user-authorization': user.token
                },
                body: JSON.stringify({
                    points: pointsInput
                })
            });

            if (response.ok) {
                const data = await response.json();
                // Redirect to Stripe checkout
                window.location.href = data.checkout_url;
            } else {
                const errorData = await response.json();
                throw new Error(errorData.detail || "Failed to create checkout session");
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error("Checkout error:", error);
            setError(error.message || "An error occurred during checkout");
        } finally {
            setLoading(false);
        }
    };

    // Don't render the page until auth is checked
    if (!isAuthChecked) {
        return (
            <Layout>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                    <CircularProgress />
                </Box>
            </Layout>
        );
    }

    return (
        <Layout>
            <Box sx={{ width: '100%', px: 2, mt: 0 }}>
                <Box sx={{ backgroundColor: "#f5f5f5", p: 4, borderRadius: 2, mb: 2 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                        Billing & Usage
                    </Typography>
                    <Typography variant="body1" sx={{ color: "rgba(0, 0, 0, 0.6)" }}>
                        Home • Billing
                    </Typography>
                </Box>

                {/* Tabs */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                    <Tabs value={activeTab} onChange={handleTabChange} aria-label="billing tabs">
                        <Tab 
                            icon={<SubscriptionsIcon />} 
                            label="Subscriptions" 
                            iconPosition="start"
                        />
                        <Tab 
                            icon={<ShoppingCartIcon />} 
                            label="One-time Purchase" 
                            iconPosition="start"
                        />
                        <Tab 
                            icon={<HistoryIcon />} 
                            label="Payment History" 
                            iconPosition="start"
                        />
                    </Tabs>
                </Box>

                {/* Subscriptions Tab */}
                {activeTab === 0 && (
                    <Box>
                        {subscriptionError && (
                            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubscriptionError(null)}>
                                {subscriptionError}
                            </Alert>
                        )}

                        {userSubscriptions && (
                            <Box sx={{ mb: 4 }}>
                                <SubscriptionManagement 
                                    subscriptions={[userSubscriptions]}
                                    onSubscriptionUpdate={() => {
                                        fetchUserSubscriptions();
                                        fetchSubscriptionHistory();
                                    }}
                                />
                            </Box>
                        )}

                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <SubscriptionPlans 
                                    userSubscriptions={userSubscriptions ? [userSubscriptions] : []}
                                    loading={loadingSubscriptions}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                )}

                {/* One-time Purchase Tab */}
                {activeTab === 1 && (
                <Grid container spacing={3}>
                    {/* Current Plan */}
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Current Plan
                                </Typography>

                                {token ? (
                                    <>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                            <Typography variant="body1">Plan:</Typography>
                                            <Typography variant="body1" fontWeight="bold">
                                                {token.plan}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                            <Typography variant="body1">Available Credits:</Typography>
                                            <Typography variant="body1" fontWeight="bold">
                                                {token.points.toLocaleString()}
                                            </Typography>
                                        </Box>

                                        {token.expires_at && (
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                                <Typography variant="body1">Expires:</Typography>
                                                <Typography variant="body1" fontWeight="bold">
                                                    {dayjs(token.expires_at).format('YYYY-MM-DD')}
                                                </Typography>
                                            </Box>
                                        )}
                                    </>
                                ) : (
                                    <Typography variant="body1">
                                        No active plan. Purchase credits below to get started.
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                                            {/* Purchase Credits */}
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Purchase API Credits
                                </Typography>

                                {!hasTokens && (
                                    <Alert severity="warning" sx={{ mb: 2 }}>
                                        You need to create at least one API token before making a purchase.
                                        Please go to the <Button
                                            variant="text"
                                            color="inherit"
                                            sx={{ fontWeight: 'bold', p: 0, minWidth: 'auto', textDecoration: 'underline' }}
                                            onClick={() => navigate('/tokens')}
                                        >
                                            Tokens page
                                        </Button> to create a token first.
                                    </Alert>
                                )}

                                {/* Pricing tiers information */}
                                <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Pricing Tiers
                                    </Typography>
                                    <Grid container spacing={1}>
                                        <Grid item xs={6}>
                                            <Typography variant="body2">1-1,000 credits:</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" fontWeight="medium">$0.04 each</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2">1,001-10,000 credits:</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" fontWeight="medium">$0.035 each</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2">10,001-50,000 credits:</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" fontWeight="medium">$0.03 each</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2">50,001+ credits:</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" fontWeight="medium">$0.025 each</Typography>
                                        </Grid>
                                        <Grid item xs={12} sx={{ mt: 1 }}>
                                            <Typography variant="caption" color="text.secondary">
                                                Minimum purchase: {minPoints} credits (${formatCurrency(10)})
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Box>

                                {error && (
                                    <Alert severity="error" sx={{ mb: 2 }}>
                                        {error}
                                    </Alert>
                                )}

                                <Box sx={{ mb: 3 }}>
                                    <TextField
                                        label="Number of Credits"
                                        type="number"
                                        value={pointsInput}
                                        onChange={handlePointsChange}
                                        fullWidth
                                        InputProps={{
                                            inputProps: {
                                                min: 1
                                            }
                                        }}
                                        helperText={`Minimum ${minPoints} credits (${formatCurrency(10)} minimum payment)`}
                                        error={belowMinimum}
                                    />
                                </Box>

                                {belowMinimum && (
                                    <Alert severity="warning" sx={{ mb: 2 }}>
                                        Minimum purchase is {minPoints} credits (${formatCurrency(10)} minimum payment)
                                    </Alert>
                                )}

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                                    <Typography variant="body1">Price per Credit:</Typography>
                                    <Typography variant="body1">
                                        {pointsInput <= 1000 && "$0.04"}
                                        {pointsInput > 1000 && pointsInput <= 10000 && "$0.035"}
                                        {pointsInput > 10000 && pointsInput <= 50000 && "$0.03"}
                                        {pointsInput > 50000 && "$0.025"}
                                    </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                                    <Typography variant="body1" fontWeight="bold">Estimated Total:</Typography>
                                    <Typography variant="body1" fontWeight="bold">
                                        {formatCurrency(estimatedPrice)}
                                    </Typography>
                                </Box>

                                <Button
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                    startIcon={<CreditCardIcon />}
                                    onClick={handlePurchase}
                                    disabled={loading || !pointsInput || !hasTokens || belowMinimum}
                                >
                                    {loading ? "Processing..." : "Purchase Credits"}
                                </Button>

                                {!hasTokens && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                                        You must create an API token before you can purchase credits
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>


                    </Grid>
                )}

                {/* Payment History Tab */}
                {activeTab === 2 && (
                    <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                    All Payment History
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                    Complete history of all payments including one-time purchases and subscription billing
                            </Typography>

                                {(loadingHistory || loadingSubscriptionHistory) ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                    <CircularProgress />
                                </Box>
                                ) : (!paymentHistory || paymentHistory.length === 0) && (!subscriptionHistory || subscriptionHistory.length === 0) ? (
                                <Box sx={{ textAlign: 'center', p: 4 }}>
                                    <CreditCardOffIcon style={{ fontSize: 48, opacity: 0.5, marginBottom: 16 }} />
                                    <Typography color="textSecondary">
                                        No payment history yet
                                    </Typography>
                                </Box>
                            ) : (
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Date</TableCell>
                                                    <TableCell>Type</TableCell>
                                                <TableCell>Amount</TableCell>
                                                    <TableCell>Credits</TableCell>
                                                <TableCell>Status</TableCell>
                                                    <TableCell>Receipt</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                                {/* Combine and sort all payments by date */}
                                                {[
                                                    // One-time payments
                                                    ...(paymentHistory || []).map(payment => ({
                                                        ...payment,
                                                        type: 'one-time',
                                                        sortDate: new Date(payment.created_at)
                                                    })),
                                                    // Subscription payments
                                                    ...(subscriptionHistory || []).map(payment => ({
                                                        ...payment,
                                                        type: 'subscription',
                                                        sortDate: new Date(payment.created_at)
                                                    }))
                                                ]
                                                .sort((a, b) => b.sortDate - a.sortDate) // Sort newest first
                                                .map((payment) => (
                                                    <TableRow key={payment.type === 'one-time' ? payment.id : `sub-${payment.id}`}>
                                                    <TableCell>
                                                        {dayjs(payment.created_at).format('YYYY-MM-DD HH:mm')}
                                                    </TableCell>
                                                    <TableCell>
                                                            <Box
                                                                sx={{
                                                                    display: 'inline-block',
                                                                    px: 1,
                                                                    py: 0.5,
                                                                    borderRadius: 1,
                                                                    fontSize: '0.75rem',
                                                                    fontWeight: 'medium',
                                                                    bgcolor: payment.type === 'one-time' ? '#E3F2FD' : '#F3E5F5',
                                                                    color: payment.type === 'one-time' ? '#1565C0' : '#7B1FA2',
                                                                }}
                                                            >
                                                                {payment.type === 'one-time' 
                                                                    ? 'One-time Purchase' 
                                                                    : `Subscription (${payment.plan_name})`
                                                                }
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                            {payment.type === 'one-time' 
                                                                ? formatCurrency(payment.amount)
                                                                : formatCurrency(payment.amount_paid / 100)
                                                            }
                                                    </TableCell>
                                                    <TableCell>
                                                            {payment.type === 'one-time' 
                                                                ? payment.points.toLocaleString()
                                                                : payment.credits_allocated.toLocaleString()
                                                            }
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box
                                                            sx={{
                                                                display: 'inline-block',
                                                                px: 1,
                                                                py: 0.5,
                                                                borderRadius: 1,
                                                                fontSize: '0.75rem',
                                                                fontWeight: 'medium',
                                                                    bgcolor: (payment.status === 'completed' || payment.status === 'paid') ? '#E6F4EA' : '#FFFDE7',
                                                                    color: (payment.status === 'completed' || payment.status === 'paid') ? '#1B5E20' : '#FBC02D',
                                                            }}
                                                        >
                                                            {payment.status}
                                                        </Box>
                                                    </TableCell>
                                                        <TableCell>
                                                            {payment.stripe_receipt_url ? (
                                                                <Button
                                                                    size="small"
                                                                    startIcon={<OpenInNewIcon />}
                                                                    onClick={() => window.open(payment.stripe_receipt_url, '_blank')}
                                                                    sx={{ fontSize: '0.75rem' }}
                                                                >
                                                                    View Receipt
                                                                </Button>
                                                            ) : (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    N/A
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Paper>
                    </Grid>
                </Grid>
                )}



                {/* Subscription Payment Modal */}
                
            </Box>
        </Layout>
    );
};

export default BillingPage;